import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { 
  Flower2, 
  ArrowLeft, 
  Trash2,
  Palette,
  MessageSquare,
  BookOpen,
  Sparkles,
  Check,
  Download,
  Package,
  ExternalLink,
  Globe,
  Eye,
  Heart,
  Lock,
  FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = "Ae_O9morsIW0sXAH4lUgaH2epHecxsbaHN4li4kllPElnAwozy9p20-0A0ttpeIrBggowDjYnz9KRVnA";

// Helper to get color palette as array (handles both old and new format)
const getColorPaletteArray = (colorPalette) => {
  if (!colorPalette) return [];
  // New format: object with colors array
  if (colorPalette.colors && Array.isArray(colorPalette.colors)) {
    return colorPalette.colors;
  }
  // Old format: direct array
  if (Array.isArray(colorPalette)) {
    return colorPalette;
  }
  return [];
};

// Helper to get brand values as array of strings (handles both old and new format)
const getBrandValuesArray = (brandValues) => {
  if (!brandValues) return [];
  if (Array.isArray(brandValues)) {
    // Check if it's new format (array of objects with value/proof)
    if (brandValues.length > 0 && typeof brandValues[0] === 'object' && brandValues[0].value) {
      return brandValues.map(v => v.value);
    }
    // Old format: array of strings
    return brandValues;
  }
  return [];
};

const BrandDetail = () => {
  const navigate = useNavigate();
  const { brandId } = useParams();
  const { user } = useContext(AuthContext);
  const [brand, setBrand] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isCheckingPurchase, setIsCheckingPurchase] = useState(false);
  const [showDownloadNotice, setShowDownloadNotice] = useState(false);

  // Check if user has seen download notice
  const hasSeenNotice = localStorage.getItem('bloomwright_download_notice_seen') === 'true';

  useEffect(() => {
    fetchBrand();
  }, [brandId]);

  // Check purchase status when brand is loaded
  useEffect(() => {
    if (brand?.brand_id) {
      checkPurchaseStatus(brand.brand_id);
    }
  }, [brand?.brand_id]);

  const fetchBrand = async () => {
    try {
      const response = await axios.get(`${API}/brands/${brandId}`);
      setBrand(response.data);
    } catch (error) {
      console.error("Error fetching brand:", error);
      toast.error("Brand not found");
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const checkPurchaseStatus = async (brandId) => {
    setIsCheckingPurchase(true);
    try {
      const response = await axios.get(`${API}/brands/${brandId}/purchase-status`);
      setHasPurchased(response.data.purchased);
    } catch (error) {
      console.error("Error checking purchase status:", error);
    } finally {
      setIsCheckingPurchase(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this brand?")) return;
    
    try {
      await axios.delete(`${API}/brands/${brandId}`);
      toast.success("Brand deleted successfully");
      navigate("/dashboard");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete brand");
    }
  };

  // Handle download button click
  const handleDownloadClick = () => {
    if (!hasPurchased) {
      setShowCheckout(true);
      return;
    }
    
    // Show notice if user hasn't seen it before
    if (!hasSeenNotice) {
      setShowDownloadNotice(true);
      return;
    }
    
    // Proceed with download
    triggerDownload();
  };

  // Dismiss notice and proceed with download
  const dismissNoticeAndDownload = () => {
    localStorage.setItem('bloomwright_download_notice_seen', 'true');
    setShowDownloadNotice(false);
    triggerDownload();
  };

  // Trigger the actual download - opens in new tab
  const triggerDownload = () => {
    if (!brand?.brand_id) return;
    window.open(`/api/brand-guide.pdf?brand_id=${brand.brand_id}`, '_blank');
  };

  // Create PayPal order
  const createOrder = async () => {
    try {
      const response = await axios.post(`${API}/brands/checkout`, {
        brand_id: brand.brand_id
      });
      
      if (response.data.already_purchased) {
        setHasPurchased(true);
        setShowCheckout(false);
        toast.success("You already own this Brand Guide!");
        return null;
      }
      
      return response.data.order_id;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout. Please try again.");
      throw error;
    }
  };

  // Capture PayPal payment
  const onApprove = async (data) => {
    try {
      const response = await axios.post(`${API}/brands/checkout/capture`, {
        order_id: data.orderID
      });
      
      if (response.data.success) {
        setHasPurchased(true);
        setShowCheckout(false);
        toast.success("Payment successful! You can now download your Brand Guide.");
      }
    } catch (error) {
      console.error("Capture error:", error);
      toast.error("Payment capture failed. Please contact support.");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!brand) {
    return null;
  }

  return (
    <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD" }}>
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#C77D63]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-[#C77D63]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2C332E] mb-2">
                Your Brand Guide
              </h3>
              <p className="text-[#78716C] text-sm">
                To download your Brand Guide PDF, continue to checkout.
              </p>
            </div>

            {/* What's Included */}
            <div className="bg-[#FDFCF8] rounded-xl p-4 mb-6">
              <h4 className="text-sm font-semibold text-[#2C332E] mb-3">Included with Your Purchase</h4>
              <ul className="space-y-2 text-sm text-[#78716C]">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                  A downloadable Brand Guide PDF
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                  Your brand name, positioning, and tone
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                  Color palette and typography direction
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#8DA399] mt-0.5 flex-shrink-0" />
                  Logo style and packaging guidance
                </li>
              </ul>
            </div>

            {/* Product & Price */}
            <div className="border border-[#E6E1DB] rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-[#2C332E]">Brand Guide</span>
                <span className="font-semibold text-[#2C332E]">$47</span>
              </div>
              <p className="text-xs text-[#78716C]">
                A calm, structured snapshot of your brand — delivered as a downloadable PDF.
              </p>
            </div>

            {/* PayPal Buttons */}
            <div className="mb-4">
              <PayPalButtons
                style={{ layout: "vertical", shape: "pill" }}
                createOrder={createOrder}
                onApprove={onApprove}
                onError={(err) => {
                  console.error("PayPal error:", err);
                  toast.error("Payment failed. Please try again.");
                }}
              />
            </div>

            {/* Close */}
            <button
              onClick={() => setShowCheckout(false)}
              className="w-full text-sm text-[#78716C] hover:text-[#2C332E] py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-[#2C332E] hover:bg-[#F5F0EB]"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Dashboard
          </Button>
          <div className="flex items-center">
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Download button - changes based on purchase state */}
            {!hasPurchased ? (
              <Button 
                variant="outline"
                onClick={() => setShowCheckout(true)}
                disabled={isCheckingPurchase}
                className="border-[#C77D63] text-[#C77D63] hover:bg-[#C77D63]/5 rounded-full"
                data-testid="unlock-brand-btn"
              >
                {isCheckingPurchase ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2 border-2 border-[#C77D63] border-t-transparent rounded-full animate-spin"></div>
                    Checking...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Unlock Brand Guide — $47
                  </>
                )}
              </Button>
            ) : (
              <Button 
                variant="outline"
                onClick={handleDownloadClick}
                className="border-[#C77D63] text-[#C77D63] hover:bg-[#C77D63]/5 rounded-full"
                data-testid="download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Brand Guide (PDF)
              </Button>
            )}
            <Button 
              variant="ghost"
              onClick={handleDelete}
              className="text-red-500 hover:bg-red-50 rounded-full"
              data-testid="delete-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Global Download Notice - shown once per user */}
      {showDownloadNotice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#8DA399]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-[#8DA399]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2C332E] mb-3">
                Your Brand Guide Is Ready
              </h3>
              <p className="text-[#78716C] mb-6 leading-relaxed">
                Your complete brand guide has been prepared and is ready to view.
              </p>
              <p className="text-[#78716C] mb-6 leading-relaxed text-sm">
                Open the guide in a new tab to review, save, or download it when you're ready.
              </p>
              <Button 
                onClick={dismissNoticeAndDownload}
                className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-3 w-full"
                data-testid="open-pdf-btn"
              >
                Open Brand Guide (PDF)
              </Button>
              <p className="text-xs text-[#A8A29E] mt-4 leading-relaxed">
                You can download the PDF directly from your browser once it opens.
                Opening in a new tab ensures uninterrupted access.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Brand Name & Tagline */}
        <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft mb-8 overflow-hidden">
          {/* Color Palette Preview */}
          <div className="h-4 flex">
            {getColorPaletteArray(brand.color_palette).map((color, index) => (
              <div 
                key={index}
                className="flex-1"
                style={{ backgroundColor: color.hex }}
              />
            ))}
          </div>
          <CardContent className="p-8 text-center">
            <p className="text-sm text-[#8DA399] uppercase tracking-wide mb-2">
              {brand.business_type?.replace(/_/g, ' ')}
            </p>
            <h1 className="text-4xl md:text-5xl tracking-tight text-[#2C332E] mb-4" data-testid="brand-name">
              {brand.brand_name}
            </h1>
            <p className="text-xl text-[#78716C] italic" data-testid="brand-tagline">
              &ldquo;{brand.tagline}&rdquo;
            </p>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="palette" className="space-y-6">
          <TabsList className="bg-[#F5F0EB] p-1 rounded-full w-full justify-start overflow-x-auto">
            <TabsTrigger 
              value="palette" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <Palette className="w-4 h-4 mr-2" />
              Color Palette
            </TabsTrigger>
            <TabsTrigger 
              value="tone" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Tone & Values
            </TabsTrigger>
            <TabsTrigger 
              value="story" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Brand Story
            </TabsTrigger>
            <TabsTrigger 
              value="guide" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Business Guide
            </TabsTrigger>
            <TabsTrigger 
              value="packaging" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <Package className="w-4 h-4 mr-2" />
              Packaging
            </TabsTrigger>
          </TabsList>

          {/* Color Palette Tab */}
          <TabsContent value="palette">
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2C332E]">Your Color Palette</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-5 gap-4">
                  {getColorPaletteArray(brand.color_palette).map((color, index) => (
                    <div key={index} className="text-center" data-testid={`color-${index}`}>
                      <div 
                        className="w-full aspect-square rounded-2xl mb-3 shadow-soft cursor-pointer hover:scale-105 transition-transform"
                        style={{ backgroundColor: color.hex }}
                        onClick={() => {
                          navigator.clipboard.writeText(color.hex);
                          toast.success(`Copied ${color.hex}`);
                        }}
                      />
                      <p className="font-semibold text-[#2C332E]">{color.name}</p>
                      <p className="text-sm text-[#78716C] uppercase">{color.hex}</p>
                      <p className="text-xs text-[#8DA399] mt-1">{color.usage}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#78716C] text-center mt-4">Click a color to copy its hex code</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tone & Values Tab */}
          <TabsContent value="tone">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#2C332E]">Tone of Voice</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#78716C] leading-relaxed" data-testid="tone-of-voice">
                    {brand.tone_of_voice}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#2C332E]">Brand Values</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {getBrandValuesArray(brand.brand_values).map((value, index) => (
                      <span 
                        key={index}
                        className="px-4 py-2 bg-[#8DA399]/10 text-[#8DA399] rounded-full flex items-center gap-2"
                        data-testid={`brand-value-${index}`}
                      >
                        <Check className="w-4 h-4" />
                        {value}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#2C332E]">Target Audience</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#78716C] leading-relaxed" data-testid="target-audience">
                    {brand.target_audience}
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Brand Story Tab */}
          <TabsContent value="story">
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2C332E]">Your Brand Story</CardTitle>
                {!hasPurchased && (
                  <div className="mt-3 p-3 bg-[#8DA399]/10 rounded-lg border border-[#8DA399]/20">
                    <p className="text-sm text-[#8DA399] font-medium flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Preview only. Full access unlocks after activation.
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent className={!hasPurchased ? "select-none" : ""}>
                <div className="prose max-w-none">
                  <p className="text-[#78716C] leading-relaxed whitespace-pre-line" data-testid="brand-story">
                    {brand.brand_story}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Guide Tab */}
          <TabsContent value="guide">
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2C332E]">Business Launch Guide</CardTitle>
                <p className="text-[#78716C] mt-1">Your step-by-step path from brand idea to first launch</p>
                {!hasPurchased && (
                  <div className="mt-3 p-3 bg-[#8DA399]/10 rounded-lg border border-[#8DA399]/20">
                    <p className="text-sm text-[#8DA399] font-medium flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Preview only. Full access unlocks after activation.
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent className={!hasPurchased ? "select-none" : ""}>
                {/* Preview Mode Indicator */}
                {!hasPurchased && (
                  <div className="mb-6 text-center">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5F0EB] rounded-full text-sm text-[#78716C]">
                      <Eye className="w-4 h-4" />
                      Read-Only Preview Mode
                    </span>
                  </div>
                )}

                {(() => {
                  const bg = brand?.business_guide;
                  
                  // New format: object with phase1_offer, next_step, etc.
                  if (bg && typeof bg === 'object' && !Array.isArray(bg)) {
                    return (
                      <div className="space-y-6">
                        <p className="text-[#78716C] mb-4">A calm framework for moving forward — without pressure.</p>
                        {bg.phase1_offer && (
                          <div className={`p-5 rounded-xl bg-[#F5F0EB]/50 border border-[#E6E1DB]/30 ${!hasPurchased ? 'pointer-events-none' : ''}`}>
                            <div className="flex items-start gap-4 mb-2">
                              <div className="step-circle completed flex-shrink-0">1</div>
                              <div>
                                <h4 className="text-lg font-semibold text-[#2C332E]">Start With</h4>
                              </div>
                            </div>
                            <p className="text-[#78716C] leading-relaxed pl-12">{bg.phase1_offer}</p>
                          </div>
                        )}
                        {bg.next_step && (
                          <div className={`p-5 rounded-xl bg-[#F5F0EB]/50 border border-[#E6E1DB]/30 ${!hasPurchased ? 'pointer-events-none' : ''}`}>
                            <div className="flex items-start gap-4 mb-2">
                              <div className="step-circle completed flex-shrink-0">2</div>
                              <div>
                                <h4 className="text-lg font-semibold text-[#2C332E]">One Next Step This Week</h4>
                              </div>
                            </div>
                            <p className="text-[#78716C] leading-relaxed pl-12">{bg.next_step}</p>
                          </div>
                        )}
                        {bg.starter_channel && (
                          <div className={`p-5 rounded-xl bg-[#F5F0EB]/50 border border-[#E6E1DB]/30 ${!hasPurchased ? 'pointer-events-none' : ''}`}>
                            <div className="flex items-start gap-4 mb-2">
                              <div className="step-circle completed flex-shrink-0">3</div>
                              <div>
                                <h4 className="text-lg font-semibold text-[#2C332E]">One Channel to Begin With</h4>
                              </div>
                            </div>
                            <p className="text-[#78716C] leading-relaxed pl-12">{bg.starter_channel}</p>
                          </div>
                        )}
                        {bg.milestone && (
                          <div className={`p-5 rounded-xl bg-[#F5F0EB]/50 border border-[#E6E1DB]/30 ${!hasPurchased ? 'pointer-events-none' : ''}`}>
                            <div className="flex items-start gap-4 mb-2">
                              <div className="step-circle completed flex-shrink-0">4</div>
                              <div>
                                <h4 className="text-lg font-semibold text-[#2C332E]">A Calm First Milestone</h4>
                              </div>
                            </div>
                            <p className="text-[#78716C] leading-relaxed pl-12">{bg.milestone}</p>
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Old format: array of steps
                  if (Array.isArray(bg)) {
                    return (
                      <div className="space-y-8">
                        {bg.map((step, index) => (
                          <div 
                            key={index}
                            className={`p-5 rounded-xl bg-[#F5F0EB]/50 border border-[#E6E1DB]/30 ${!hasPurchased ? 'pointer-events-none' : ''}`}
                            data-testid={`guide-step-${index}`}
                          >
                            <div className="flex items-start gap-4 mb-4">
                              <div className="step-circle completed flex-shrink-0">
                                {step.step}
                              </div>
                              <div>
                                <h4 className="text-lg font-semibold text-[#2C332E]">{step.title}</h4>
                                {step.purpose && (
                                  <p className="text-sm text-[#C77D63] mt-1 font-medium">Purpose: {step.purpose}</p>
                                )}
                              </div>
                            </div>
                            <p className="text-[#78716C] leading-relaxed mb-4 pl-12">{step.description}</p>
                            {step.outcome && (
                              <div className="pl-12 pt-3 border-t border-[#E6E1DB]/50">
                                <p className="text-sm">
                                  <span className="font-semibold text-[#8DA399]">Outcome:</span>{" "}
                                  <span className="text-[#78716C]">{step.outcome}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  }
                  
                  // Fallback
                  return <p className="text-[#78716C]">Business guide coming soon...</p>;
                })()}

                {/* Closing Note */}
                <div className="mt-10 p-6 bg-gradient-to-br from-[#8DA399]/10 to-[#C77D63]/10 rounded-2xl text-center">
                  <p className="text-[#2C332E] font-medium italic leading-relaxed">
                    You don&apos;t need everything figured out before you begin.<br />
                    This guide is here to help you take one clear, intentional step at a time.
                  </p>
                </div>

                {/* Purchase CTA for users who haven't bought yet */}
                {!hasPurchased && (
                  <div className="mt-8 p-6 bg-gradient-to-r from-[#C77D63]/10 to-[#8DA399]/10 rounded-2xl text-center border border-[#E6E1DB]">
                    <p className="text-[#2C332E] font-medium mb-2">Ready to download your Brand Guide?</p>
                    <p className="text-sm text-[#78716C] mb-4">Unlock your complete brand overview PDF.</p>
                    <Button
                      onClick={() => setShowCheckout(true)}
                      className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-6"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Unlock Brand Guide — $47
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packaging Vendors Tab */}
          <TabsContent value="packaging">
            <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2C332E]">Recommended Packaging Vendors</CardTitle>
                {!hasPurchased && (
                  <div className="mt-3 p-3 bg-[#8DA399]/10 rounded-lg border border-[#8DA399]/20">
                    <p className="text-sm text-[#8DA399] font-medium flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Preview only. Full access unlocks after activation.
                    </p>
                  </div>
                )}
                <p className="text-[#78716C] mt-2">Trusted suppliers for your {brand.business_type?.replace(/_/g, ' ')} products</p>
              </CardHeader>
              <CardContent className={!hasPurchased ? "select-none" : ""}>
                {brand.packaging_vendors && brand.packaging_vendors.length > 0 ? (
                  <div className="grid md:grid-cols-3 gap-4">
                    {brand.packaging_vendors.map((vendor, index) => (
                      <div 
                        key={index}
                        className={`p-4 rounded-xl border border-[#E6E1DB] transition-all ${hasPurchased ? 'hover:border-[#8DA399] hover:shadow-soft' : 'pointer-events-none'}`}
                        data-testid={`vendor-${index}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold text-[#2C332E]">{vendor.name}</h4>
                          {hasPurchased && (
                            <a 
                              href={vendor.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#8DA399] hover:text-[#C77D63] transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-[#78716C]">{vendor.specialty}</p>
                        {hasPurchased ? (
                          <a 
                            href={vendor.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-[#8DA399] hover:text-[#C77D63] mt-3 transition-colors"
                          >
                            Visit Website
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <p className="text-xs text-[#8DA399] mt-3 italic">Link available after activation</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#78716C] text-center py-8">No packaging vendors saved for this brand. Create a new brand to get packaging recommendations.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Post-Save Brand Orientation Block */}
        <section className="mt-16 mb-12">
          <Card className="bg-gradient-to-br from-[#FDFCF8] to-[#F5F0EB] rounded-3xl border-[#E6E1DB]/30 shadow-soft overflow-hidden">
            <CardContent className="p-8 md:p-12">
              {/* Section Header */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#8DA399]/10 mb-4">
                  <Sparkles className="w-6 h-6 text-[#8DA399]" />
                </div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#2C332E] mb-3">
                  Begin With Clarity
                </h2>
                <p className="text-[#78716C] max-w-2xl mx-auto leading-relaxed">
                  Your brand guide is saved. There&apos;s nothing you need to act on immediately.
                </p>
                <p className="text-[#78716C] max-w-2xl mx-auto leading-relaxed mt-3">
                  This stage is about understanding — not execution.<br />
                  When you&apos;re ready, here are a few gentle ways to begin working with what you&apos;ve created.
                </p>
              </div>

              {/* Orientation Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {/* Card 1 - Use it visually */}
                <div className="bg-white rounded-2xl p-6 shadow-soft">
                  <div className="w-10 h-10 rounded-xl bg-[#C77D63]/10 flex items-center justify-center mb-4">
                    <Palette className="w-5 h-5 text-[#C77D63]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#2C332E] mb-3">Use it visually</h3>
                  <p className="text-[#78716C] text-sm leading-relaxed mb-3">
                    Your colors, tone, and overall direction are now defined.
                  </p>
                  <p className="text-[#78716C] text-sm leading-relaxed mb-4">
                    Use them as a reference when exploring simple visuals — a draft post, a label idea, or a loose mood direction — without building a full design system.
                  </p>
                  <p className="text-xs text-[#8DA399] italic mb-4">
                    This is about recognition, not polish.
                  </p>
                  <p className="text-xs text-[#A8A29E] bg-[#F5F0EB] rounded-lg p-2">
                    This guide provides direction only. Finished templates and assets are available in the Brand Foundation Kits.
                  </p>
                </div>

                {/* Card 2 - Use it in words */}
                <div className="bg-white rounded-2xl p-6 shadow-soft">
                  <div className="w-10 h-10 rounded-xl bg-[#8DA399]/10 flex items-center justify-center mb-4">
                    <MessageSquare className="w-5 h-5 text-[#8DA399]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#2C332E] mb-3">Use it in words</h3>
                  <p className="text-[#78716C] text-sm leading-relaxed mb-3">
                    Your brand story and values can guide how you describe what you do — on a website draft, a shop outline, or even a short bio.
                  </p>
                  <p className="text-[#78716C] text-sm leading-relaxed mb-3">
                    You don&apos;t need to publish anything yet.<br />
                    This stage is about clarity, not visibility.
                  </p>
                  <p className="text-xs text-[#8DA399] italic mb-4">
                    Use your words as working drafts, not final copy.
                  </p>
                  <p className="text-xs text-[#A8A29E] bg-[#F5F0EB] rounded-lg p-2">
                    This package does not include website setup, publishing tools, or launch execution.
                  </p>
                </div>

                {/* Card 3 - Use it as packaging reference */}
                <div className="bg-white rounded-2xl p-6 shadow-soft">
                  <div className="w-10 h-10 rounded-xl bg-[#2C332E]/10 flex items-center justify-center mb-4">
                    <Package className="w-5 h-5 text-[#2C332E]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#2C332E] mb-3">Use it as a packaging reference</h3>
                  <p className="text-[#78716C] text-sm leading-relaxed mb-3">
                    This brand was designed to translate into the physical world when the time comes.
                  </p>
                  <p className="text-[#78716C] text-sm leading-relaxed mb-3">
                    Your colors, tone, and values can guide packaging decisions that feel aligned — even for small batches or simple labels.
                  </p>
                  <p className="text-[#78716C] text-sm leading-relaxed mb-3">
                    You&apos;re not choosing packaging today. You&apos;re learning what fits.
                  </p>
                  <p className="text-xs text-[#8DA399] italic">
                    Clear direction now prevents costly decisions later.
                  </p>
                </div>
              </div>

              {/* Closing Line */}
              <div className="text-center pt-6 border-t border-[#E6E1DB]/50">
                <p className="text-[#78716C] text-sm leading-relaxed max-w-2xl mx-auto">
                  If you want more structure, tools, or a ready-made starting point, you can explore the <span className="font-medium text-[#2C332E]">Brand Foundation Kit ($197)</span> or a <span className="font-medium text-[#2C332E]">Ready-Made Brand ($297)</span> when the timing feels right.
                </p>
                <p className="text-[#8DA399] text-sm font-medium mt-3">
                  No pressure. No urgency. Just orientation.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
    </PayPalScriptProvider>
  );
};

export default BrandDetail;
